import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight, Play } from "lucide-react";
import api from "../lib/api";
import { EditableText } from "./live/Editable";
import BatchCountdown from "./BatchCountdown";

export default function HeroSection() {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get("/banners", { params: { active_only: true } })
      .then((r) => setBanners(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 5500);
    return () => clearInterval(t);
  }, [banners.length]);

  const current = banners[idx] || {
    title: "Build Your Future with German Precision",
    subtitle: "Industry-recognized technical training certified by HWK Koblenz, Germany.",
    cta_text: "Apply Now",
    cta_link: "/admissions",
    image_url: "/assets/banners/banner1.jpg",
  };

  return (
    <section className="relative overflow-hidden gradient-hero text-white">
      {/* Decorative animated shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400/20 animate-blob blur-3xl" />
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-blue-400/20 animate-blob blur-3xl" style={{ animationDelay: "2s" }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-yellow-300/10 animate-blob blur-3xl" style={{ animationDelay: "4s" }} />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 lg:pt-20 lg:pb-40">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text content (left) */}
          <div className="relative z-10">
            <EditableText
              path="home.hero.badge"
              as="div"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 text-xs font-bold tracking-wider uppercase mb-6"
            >
              Admissions Open 2026
            </EditableText>

            {/* Headline */}
            <h1 className="font-black leading-[1.05] mb-6 tracking-tight animate-fade-up">
              <EditableText
                path="home.hero.headline"
                as="span"
                className="block text-4xl sm:text-5xl lg:text-6xl text-yellow-400 drop-shadow-[0_2px_20px_rgba(250,204,21,0.35)]"
              >
                Earn While You Learn
              </EditableText>
            </h1>

            <EditableText
              path="home.hero.subtitle"
              as="p"
              multiline
              className="text-lg text-slate-200 max-w-xl leading-relaxed mb-8 animate-fade-up"
            >
              3½ Years German Technical Training Course for 10th / 12th pass students (all subjects). HWK Koblenz + Government of India dual certification.
            </EditableText>

            <div className="flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Link
                to={current.cta_link || "/admissions"}
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg btn-primary animate-pulse-glow"
              >
                <EditableText path="home.hero.cta_primary" as="span">
                  Apply Now
                </EditableText>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition" />
              </Link>
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg btn-outline-light"
              >
                <Play className="h-4 w-4 fill-current" />
                <EditableText path="home.hero.cta_secondary" as="span">
                  Explore Courses
                </EditableText>
              </Link>
            </div>

            {/* Batch countdown */}
            <BatchCountdown />

            {/* Stats strip */}
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "students", n: "5K+", l: "Students Trained" },
                { k: "placement", n: "95%", l: "Placement Rate" },
                { k: "partners", n: "60+", l: "Industry Partners" },
              ].map((s) => (
                <div key={s.k}>
                  <EditableText path={`home.hero.stat.${s.k}.value`} as="div" className="text-2xl lg:text-3xl font-bold text-yellow-400">
                    {s.n}
                  </EditableText>
                  <EditableText path={`home.hero.stat.${s.k}.label`} as="div" className="text-xs text-slate-300 mt-1">
                    {s.l}
                  </EditableText>
                </div>
              ))}
            </div>
          </div>

          {/* Image stack (right) - 3D effect */}
          <div className="relative h-[440px] lg:h-[520px] hidden lg:block perspective-1000">
            {/* Floating yellow blob */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-yellow-400 rounded-full blur-2xl opacity-50 animate-float" />
            <div className="absolute bottom-10 -left-6 w-40 h-40 bg-blue-400/40 rounded-full blur-2xl animate-float" style={{ animationDelay: "2s" }} />

            {/* Yellow accent panel behind (3D depth) */}
            <div className="absolute inset-0 translate-x-5 translate-y-5 rounded-3xl bg-gradient-to-br from-yellow-400 to-yellow-500 opacity-90" />
            {/* Blue accent line */}
            <div className="absolute -inset-1 rounded-3xl border-2 border-yellow-400/30 -rotate-1" />

            {banners.map((b, i) => (
              <div
                key={b.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  i === idx ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-95 rotate-1"
                }`}
              >
                <div
                  className="relative h-full w-full rounded-3xl overflow-hidden bg-[#0B2A6B]"
                  style={{
                    boxShadow:
                      "0 30px 60px -15px rgba(0, 0, 0, 0.5), 0 18px 36px -18px rgba(11, 42, 107, 0.6), inset 0 1px 0 0 rgba(255,255,255,0.08)",
                    transform: "perspective(1200px) rotateY(-4deg) rotateX(2deg)",
                  }}
                >
                  <img
                    src={b.image_url}
                    alt={b.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Image gradient overlay to ensure readability */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0B2A6B]/60 via-[#0B2A6B]/10 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Top badge */}
                  <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/95 text-[#0B2A6B] text-[11px] font-bold uppercase tracking-wider backdrop-blur">
                    <Sparkles className="h-3 w-3" />
                    Featured
                  </div>

                  {/* Floating glassy card at bottom */}
                  <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md rounded-xl p-4 shadow-2xl border border-white/40">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 shrink-0 rounded-lg bg-yellow-400 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-[#0B2A6B]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[#0B2A6B] font-bold text-sm line-clamp-1">{b.title}</div>
                        <div className="text-xs text-slate-600 line-clamp-1">{b.subtitle}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Slider controls */}
            {banners.length > 1 && (
              <div className="absolute -bottom-6 right-2 flex gap-2 z-10">
                <button
                  onClick={() => setIdx((i) => (i - 1 + banners.length) % banners.length)}
                  className="h-11 w-11 rounded-full bg-white text-[#0B2A6B] hover:bg-yellow-400 transition flex items-center justify-center shadow-xl"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % banners.length)}
                  className="h-11 w-11 rounded-full bg-yellow-400 text-[#0B2A6B] hover:bg-white transition flex items-center justify-center shadow-xl"
                  aria-label="Next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Dot indicators */}
        {banners.length > 1 && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-2 rounded-full transition-all ${
                  i === idx ? "w-8 bg-yellow-400" : "w-2 bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom wave */}
      <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 70 }}>
        <path fill="#ffffff" d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" />
      </svg>
    </section>
  );
}
